import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { 
  Shield, FileText, CheckCircle, Clock, AlertTriangle,
  Globe, BookOpen, Users, Eye, Pencil, ChevronRight
} from 'lucide-react';
import { GOVERNANCE_POLICIES } from '../../data/governanceContent';

/**
 * GovernanceSection - Displays documentation governance rules and policies
 */
export function GovernanceSection() {
  const [activePolicy, setActivePolicy] = useState('overview');

  const policies = GOVERNANCE_POLICIES;

  return (
    <div className="space-y-6" data-testid="governance-section">
      {/* Header */}
      <Card className="bg-gradient-to-r from-slate-700 to-slate-900 text-white border-0">
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Documentation Governance</h2>
              <p className="text-white/80">Standards, policies, and procedures for documentation</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Policy Navigation */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activePolicy === 'overview' ? 'default' : 'outline'}
          onClick={() => setActivePolicy('overview')}
          className="gap-2"
        >
          <BookOpen className="w-4 h-4" />
          Overview
        </Button>
        <Button
          variant={activePolicy === 'updateRules' ? 'default' : 'outline'}
          onClick={() => setActivePolicy('updateRules')}
          className="gap-2"
        >
          <Pencil className="w-4 h-4" />
          Update Rules
        </Button>
        <Button
          variant={activePolicy === 'styleGuide' ? 'default' : 'outline'}
          onClick={() => setActivePolicy('styleGuide')}
          className="gap-2"
        >
          <FileText className="w-4 h-4" />
          Style Guide
        </Button>
        <Button
          variant={activePolicy === 'languagePolicy' ? 'default' : 'outline'}
          onClick={() => setActivePolicy('languagePolicy')}
          className="gap-2"
        >
          <Globe className="w-4 h-4" />
          Language Policy
        </Button>
      </div>

      {/* Overview Section */}
      {activePolicy === 'overview' && policies.overview && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{policies.overview.title}</CardTitle>
              <CardDescription>{policies.overview.description}</CardDescription>
              <Badge variant="outline" className="w-fit mt-2">
                <Clock className="w-3 h-3 mr-1" />
                Last updated: {policies.overview.lastUpdated}
              </Badge>
            </CardHeader>
            <CardContent>
              {policies.overview.sections.map(section => (
                <div key={section.id} className="mb-6 last:mb-0">
                  <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                  <p className="text-gray-600 whitespace-pre-line">{section.content}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Update Rules Section */}
      {activePolicy === 'updateRules' && policies.updateRules && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{policies.updateRules.title}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Rules List */}
              <div className="space-y-3 mb-6">
                {policies.updateRules.rules.map(rule => (
                  <div 
                    key={rule.id} 
                    className={`p-4 rounded-lg border-l-4 ${
                      rule.priority === 'required' 
                        ? 'bg-red-50 border-red-500' 
                        : 'bg-amber-50 border-amber-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <h4 className="font-semibold text-gray-900">{rule.title}</h4>
                      <Badge className={rule.priority === 'required' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {rule.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{rule.description}</p>
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Enforcement: {rule.enforcement}
                    </p>
                  </div>
                ))}
              </div>

              {/* Review Process */}
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Review Process
              </h4>
              <div className="relative">
                {/* Process steps */}
                <div className="flex flex-wrap gap-2 items-center mb-4">
                  {policies.updateRules.reviewProcess.steps.map((step, idx) => (
                    <div key={step.step} className="flex items-center">
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-semibold text-sm">
                          {step.step}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{step.title}</p>
                          <p className="text-xs text-gray-500">{step.description}</p>
                        </div>
                      </div>
                      {idx < policies.updateRules.reviewProcess.steps.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-gray-400 mx-1 hidden sm:block" />
                      )}
                    </div>
                  ))}
                </div>

                {/* SLA Table */}
                <Card className="bg-gray-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Service Level Agreements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(policies.updateRules.reviewProcess.sla).map(([stage, time]) => (
                        <div key={stage} className="text-center">
                          <p className="text-xs text-gray-500 capitalize">{stage.replace(/([A-Z])/g, ' $1').trim()}</p>
                          <p className="font-semibold text-indigo-600">{time}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Style Guide Section */}
      {activePolicy === 'styleGuide' && policies.styleGuide && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{policies.styleGuide.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" defaultValue={policies.styleGuide.guidelines.map(g => g.category)}>
                {policies.styleGuide.guidelines.map(guideline => (
                  <AccordionItem key={guideline.category} value={guideline.category}>
                    <AccordionTrigger className="hover:no-underline">
                      <span className="font-semibold">{guideline.category}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pl-4">
                        {guideline.rules.map((rule, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{rule}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Language Policy Section */}
      {activePolicy === 'languagePolicy' && policies.languagePolicy && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{policies.languagePolicy.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(policies.languagePolicy.policies).map(([key, policy]) => (
                  <Card 
                    key={key}
                    className={`${
                      policy.policy === 'Full multilingual' 
                        ? 'border-emerald-200 bg-emerald-50' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center justify-between">
                        {policy.portal}
                        <Badge variant={policy.policy === 'Full multilingual' ? 'default' : 'secondary'}>
                          {policy.policy}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {policy.languages.map(lang => (
                          <Badge key={lang} variant="outline" className="text-xs">
                            {lang}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Reason:</span> {policy.reason}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default GovernanceSection;
